/* LogisticElementsCrudCard.tsx
    Une card affichant la gestion des éléments logistiques CA
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Inclut la facture client en colonne visuelle et l'avoir associé dans l'export CSV
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { LogisticElementData } from '../types/types';
import { logisticElementsApi } from '../services/logisticElementsApi';

/** Formatte un montant en euros (fr-FR) */
const formatCurrency = (value: number | null | undefined): string =>
	new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
		Number(value ?? 0)
	);

interface LogisticElementsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// LogisticElementsCrudCard component
export const LogisticElementsCrudCard: React.FC<
	LogisticElementsCrudCardProps
> = ({
	size = 'sm',
	returnPath = '/app/logistic-elements',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: LogisticElementData) => a.id }]
			: []),
		// Colonne "Libellé"
		{
			label: 'Libellé',
			field: 'label',
			render: (a: LogisticElementData) => a.label ?? '',
			exportValue: (a: LogisticElementData): string => a.label ?? '',
		},
		// Colonne "Affaire" uniquement si extended
		...(extended
			? [
					{
						label: 'Affaire',
						field: 'project.referenceNumber',
						render: (a: LogisticElementData) =>
							a.project ? a.project.referenceNumber : '',
						exportValue: (a: LogisticElementData): string =>
							a.project?.referenceNumber ?? '',
						linkUrl: (a: LogisticElementData) =>
							`/app/projects/${a.project?.id}/edit`,
					},
				]
			: []),
		// Colonne "Date" au format jour/mois/année
		{
			label: 'Date',
			field: 'date',
			filterType: 'date',
			render: (a: LogisticElementData) =>
				a.date ? new Date(a.date).toLocaleDateString('fr-FR') : '—',
			exportValue: (a: LogisticElementData): string =>
				a.date ? new Date(a.date).toLocaleDateString('fr-FR') : '',
		},
		// Colonne "Service" uniquement si extended
		...(extended
			? [
					{
						label: 'Service',
						field: 'service',
						render: (a: LogisticElementData) => a.service?.label ?? '',
						exportValue: (a: LogisticElementData): string =>
							a.service?.label ?? '',
					},
				]
			: []),
		// Colonne "Produit" uniquement si extended
		...(extended
			? [
					{
						label: 'Produit',
						field: 'product.label',
						render: (a: LogisticElementData) => a.product?.label ?? '',
						exportValue: (a: LogisticElementData): string =>
							a.product?.label ?? '',
					},
				]
			: []),
		// Colonne "Total HT" uniquement si extended (P.U. HT × quantité)
		...(extended
			? [
					{
						label: 'Total HT',
						field: 'none',
						render: (a: LogisticElementData) => {
							const total =
								Number(a.unitPriceExclTax ?? 0) * Number(a.quantity ?? 1);
							return formatCurrency(total);
						},
						exportValue: (a: LogisticElementData): number =>
							Number(a.unitPriceExclTax ?? 0) * Number(a.quantity ?? 1),
					},
				]
			: []),
		// Colonne "Facture client" uniquement si extended — lien vers la fiche facture
		...(extended
			? [
					{
						label: 'Facture client',
						field: 'customerInvoice.referenceNumber',
						render: (a: LogisticElementData) =>
							a.customerInvoice?.referenceNumber ?? '—',
						exportValue: (a: LogisticElementData): string =>
							a.customerInvoice?.referenceNumber ?? '',
						linkUrl: (a: LogisticElementData) =>
							a.customerInvoice?.id
								? `/app/customerInvoices/${a.customerInvoice.id}/edit`
								: '',
					},
				]
			: []),
	];

	// Colonnes visibles uniquement dans l'export Excel
	const exportOnlyColumns = [
		// P.U. HT (prix unitaire hors taxe)
		{
			label: 'P.U. HT',
			exportValue: (a: LogisticElementData): number | string =>
				a.unitPriceExclTax != null ? Number(a.unitPriceExclTax) : '',
		},
		// Quantité
		{
			label: 'Quantité',
			exportValue: (a: LogisticElementData): number | string =>
				a.quantity != null ? Number(a.quantity) : '',
		},
		// Avoir client associé (si remboursement via avoir)
		{
			label: 'Avoir',
			exportValue: (a: LogisticElementData): string =>
				a.creditNote?.referenceNumber ?? '',
		},
		// En mode sm, facture client n'est pas une colonne → l'exporter
		...(!extended
			? [
					{
						label: 'Facture client',
						exportValue: (a: LogisticElementData): string =>
							a.customerInvoice?.referenceNumber ?? '',
					},
				]
			: []),
		// Créé par
		{
			label: 'Créé par',
			exportValue: (a: LogisticElementData): string => {
				const u = a.createdBy;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		// Créé le
		{
			label: 'Créé le',
			exportValue: (a: LogisticElementData): string =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '',
		},
	];

	return (
		<GenericCrudCard<LogisticElementData>
			queryKey="logisticElements"
			title={
				size === 'sm'
					? 'Éléments logistiques'
					: 'Liste des éléments logistiques'
			}
			fetchData={(token, options) =>
				logisticElementsApi.listPaginated(token, options)
			}
			deleteItem={logisticElementsApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter un élément logistique"
			getAddPath={() => '/app/logisticElements/new'}
			getEditPath={(id) => `/app/logisticElements/${id}/edit`}
			emptyMessage="Aucun élément logistique trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faClipboardList : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default LogisticElementsCrudCard;
